import { routes } from '@/router/routes';
import { decode, encode } from 'js-base64';

export const DEFAULT_LANGUAGES = {
  cz: 'cs',
  sk: 'sk',
  hu: 'hu'
};

export function createRouteName(routeName, language) {
  return `${routeName}___${language}`;
}

export function sanitizePath(path) {
  const endsWithSlash = path.charAt(path.length - 1) === '/';
  if (path.length > 2 && endsWithSlash) {
    return path.slice(0, path.length - 1);
  }
  return path;
}

// Returns [{ path, name, meta, component }]
export function getLanguageSpecificRoutes(defaultLanguage) {
  return Object.values(routes).reduce((acc, { paths, component, name, meta: originalMeta }) => {
    const mappedPaths = Object.entries(paths).map(([languageKey, path]) => {
      const constructedPath = languageKey === defaultLanguage ? path : `/${languageKey}${path}`;
      return {
        path: sanitizePath(constructedPath),
        component,
        name: createRouteName(name, languageKey),
        meta: { ...originalMeta, languageKey }
      };
    });
    return [...acc, ...mappedPaths];
  }, []);
}

// Used for language switching in the UI
export function switchLocalePath({ route, language, defaultLanguage }) {
  const pageName = route.name.split('___')[0];
  const newPageName = createRouteName(pageName, language || defaultLanguage);

  const languageSpecificRoutes = getLanguageSpecificRoutes(defaultLanguage);

  let foundRoute = languageSpecificRoutes.find(route2 => route2.name === newPageName);

  let path = foundRoute?.path || '/';
  Object.entries(route.params).forEach(([key, val]) => {
    path = path.replace(':' + key, val);
  });

  const query = Object.entries(route.query)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  if (query) {
    path += '?' + query;
  }

  return path;
}

export function localePath({
  name,
  params = {},
  query = {},
  hash = '',
  language,
  defaultLanguage
}) {
  const languageSpecificRoutes = getLanguageSpecificRoutes(defaultLanguage);
  if (!Array.isArray(languageSpecificRoutes)) {
    return '/';
  }

  let route = languageSpecificRoutes.find(rue => rue.name === createRouteName(name, language));
  if (!route) {
    route = languageSpecificRoutes.find(rue => rue.name === createRouteName(name, defaultLanguage));
  }
  if (!route) {
    return '/';
  }

  let path = route.path;
  Object.entries(params).forEach(([key, val]) => {
    path = path.replace(':' + key, val);
  });
  const queryStr = Object.entries(query)
    .map(([key, val]) => `${key}=${val}`)
    .join('&');
  if (queryStr) {
    path += '?' + queryStr;
  }

  if (hash) {
    path += '#' + hash;
  }

  return path;
}

export function buildSard(data, urlSafe = true) {
  return encodeURIComponent(encode(JSON.stringify(data), urlSafe));
}

export const decodeSard = sard => {
  if (!sard || !sard.length) {
    return null;
  }

  try {
    return JSON.parse(decode(decodeURIComponent(sard)));
  } catch (error) {
    console.error('Failed to decode SARD:', error);
    return null;
  }
};
